import { EVENT_MEDIA_OPTIONS, type EventMediaType } from '@/constants/sponsor'
import { cn } from '@/lib/utils'
import { createMarkup } from '@/constants/event'
import { DndContext } from '@dnd-kit/core'
import {
  useBannerDragDrop,
  type AllElementPositions,
} from '@/hooks/useBannerDragDrop'
import { DraggableText } from '../DraggableText'

export interface BannerData {
  adName?: string
  adDescription?: string
  linkText?: string
  redirectionLink?: string
  sponsorLogoUrl?: string | null
  eventImageUrl?: string | null
  eventMediaType?: EventMediaType
  showTitle?: boolean
  showDescription?: boolean
  namePosition?: { x: number; y: number }
  descriptionPosition?: { x: number; y: number }
  sponsorLogoPosition?: { x: number; y: number }
  linkPosition?: { x: number; y: number }
}

interface HomeHeroSectionBannerProps {
  data: BannerData
  editable?: boolean
  onPositionChange?: (positions: AllElementPositions) => void
}

const HomeHeroSectionBanner = ({
  data,
  editable = false,
  onPositionChange,
}: HomeHeroSectionBannerProps) => {
  const {
    adName,
    adDescription,
    linkText,
    sponsorLogoUrl,
    eventImageUrl,
    eventMediaType = EVENT_MEDIA_OPTIONS.IMAGE,
    redirectionLink,
    showTitle = true,
    showDescription = true,
    namePosition: initialNamePosition,
    descriptionPosition: initialDescriptionPosition,
    sponsorLogoPosition: initialSponsorLogoPosition,
    linkPosition: initialLinkPosition,
  } = data

  const {
    containerRef,
    namePosition,
    descriptionPosition,
    sponsorLogoPosition,
    linkPosition,
    handleDragEnd,
  } = useBannerDragDrop({
    initialNamePosition,
    initialDescriptionPosition,
    initialSponsorLogoPosition,
    initialLinkPosition,
    defaultNamePosition: { x: 3.88, y: 18.67 }, // ~40px/1030px = 3.88%, ~56px/300px = 18.67%
    defaultDescriptionPosition: { x: 3.88, y: 33.33 }, // ~40px/1030px = 3.88%, ~100px/300px = 33.33%
    defaultSponsorLogoPosition: { x: 82.52, y: 80 }, // ~850px/1030px = 82.52%, ~240px/300px = 80%
    defaultLinkPosition: { x: 3.88, y: 80 }, // ~40px/1030px = 3.88%, ~240px/300px = 80%
    onPositionChange,
  })

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <section
        className={cn('bg-white w-full')}
        aria-label="Promotional banner: NorthFace Winter Collection"
      >
        {/* Main Banner Container */}
        <div
          ref={containerRef}
          className={cn(
            'relative max-w-full h-[300px] w-full  mx-auto overflow-hidden rounded-[16px] lg:rounded-[20px] group cursor-pointer transition-transform duration-300 shadow-lg bg-cover bg-center bg-no-repeat',
            !eventImageUrl && "bg-[url('/promo-banner.png')]"
          )}
          aria-labelledby="promo-heading"
        >
          {eventImageUrl ? (
            eventMediaType === EVENT_MEDIA_OPTIONS.VIDEO ? (
              <video
                src={eventImageUrl}
                className="absolute inset-0 w-full h-full object-cover object-center select-none pointer-events-none"
                autoPlay
                loop
                muted
                playsInline
                aria-label={
                  adName
                    ? `${adName} banner video background`
                    : 'Advertisement banner video background'
                }
              />
            ) : (
              <img
                src={eventImageUrl}
                alt={
                  adName
                    ? `${adName} banner background`
                    : 'Advertisement banner background'
                }
                className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-300 select-none pointer-events-none"
                draggable={false}
              />
            )
          ) : (
            <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-ss-neutral-200 to-ss-neutral-300" />
          )}
          {/* Overlay for readability (decorative) */}
          <div
            className="absolute inset-0 bg-black/20 z-0 pointer-events-none"
            aria-hidden="true"
          />

          {/* Main Text Content */}
          {showTitle && adName && (
            <DraggableText
              id="title"
              position={namePosition}
              isEditable={editable}
            >
              <p
                className="font-clash-display font-semibold wrap-anywhere"
                style={{
                  fontFamily: 'ClashDisplay, sans-serif',
                }}
                dangerouslySetInnerHTML={createMarkup(adName || '')}
              ></p>
            </DraggableText>
          )}

          {showDescription && adDescription && (
            <DraggableText
              id="description"
              position={descriptionPosition}
              isEditable={editable}
            >
              <h2
                id="promo-heading"
                className="font-clash-display font-semibold max-w-full wrap-anywhere line-clamp-6"
                dangerouslySetInnerHTML={createMarkup(adDescription || '')}
              />
            </DraggableText>
          )}

          {linkText && (
            <DraggableText
              id="button"
              position={linkPosition}
              isEditable={editable}
            >
              <a
                className="bg-black text-ss-lime-200 hover:bg-black/90 hover:text-ss-lime-100 rounded-full px-3 py-2.5  h-auto font-[ClashDisplay] font-semibold text-xs tracking-[0.4px] lg:tracking-[0.52px] transition-all duration-300 hover:scale-105 shadow-lg"
                href={redirectionLink}
                target="_blank"
                aria-label={
                  linkText
                    ? `${linkText} - advertisement call to action`
                    : 'Advertisement call to action'
                }
                rel="noopener noreferrer"
              >
                {linkText}
              </a>
            </DraggableText>
          )}

          {sponsorLogoUrl && (
            <DraggableText
              id="logo"
              position={sponsorLogoPosition}
              isEditable={editable}
            >
              <div className="flex flex-col items-end gap-0.5 lg:gap-1">
                <span className="font-clash-display font-normal text-xs lg:text-sm text-white text-right leading-normal">
                  Sponsored by
                </span>
                <div className="w-[60px] lg:w-[74px] h-8 lg:h-9 rounded-lg flex items-center justify-center shadow-sm">
                  {/* NorthFace Logo Placeholder */}
                  <img
                    src={sponsorLogoUrl ?? undefined}
                    alt="Sponsor logo"
                    className="max-h-8 max-w-20 object-contain"
                  />
                </div>
              </div>
            </DraggableText>
          )}
        </div>
      </section>
    </DndContext>
  )
}

export default HomeHeroSectionBanner
